import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { RefreshCw, Loader2, AlertTriangle, Scissors, FileText, Sparkles, Download } from "lucide-react";
import { getShow, remineShow, downloadAllUrl } from "../lib/api";
import { ProDownloadLink } from "./UpgradeModal";
import { fmt } from "../lib/format";
import { ClipCard } from "./ClipCard";
import MomentFinder from "./MomentFinder";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const STAGES = ["downloading", "transcribing", "analyzing", "cutting", "completed"];

export const ShowView = ({ showId, onChanged }) => {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("clips");
  const [remining, setRemining] = useState(false);

  const load = useCallback(async () => {
    if (!showId) return;
    try {
      const d = await getShow(showId);
      setData(d);
    } catch (e) { /* ignore */ }
  }, [showId]);

  useEffect(() => { setData(null); load(); }, [showId, load]);

  useEffect(() => {
    if (!data) return;
    const working = !["completed", "failed"].includes(data.show.status);
    if (!working) return;
    const t = setInterval(() => { load(); onChanged?.(); }, 3000);
    return () => clearInterval(t);
  }, [data, load, onChanged]);

  const doRemine = async () => {
    setRemining(true);
    try {
      await remineShow(showId);
      toast.success("Re-mining this show from the archive");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error("Could not re-mine");
    } finally {
      setRemining(false);
    }
  };

  const pollAfterCut = useCallback(() => {
    let n = 0;
    const t = setInterval(async () => {
      n += 1;
      await load();
      if (n >= 20) clearInterval(t);
    }, 4000);
  }, [load]);

  if (!showId) return null;
  if (!data) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const { show, clips } = data;
  const working = !["completed", "failed"].includes(show.status);
  const stageIdx = STAGES.indexOf(show.status === "pending" ? "downloading" : show.status);

  return (
    <div data-testid="show-view" className="space-y-8">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4 border-b border-border pb-6">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-primary">
            {show.source_type === "url" ? "Ingested Link" : "Uploaded Set"}
          </p>
          <h2 className="mt-1 font-display text-4xl tracking-wide text-white sm:text-5xl">{show.title}</h2>
          <div className="mt-2 flex flex-wrap gap-3 font-mono text-xs text-muted-foreground">
            {show.duration_sec > 0 && <span>RUNTIME {fmt(show.duration_sec)}</span>}
            {show.status === "completed" && <span className="text-accent">{clips.length} CLIPS MINED</span>}
          </div>
        </div>
        {show.status === "completed" && (
          <div className="flex items-center gap-2">
            <ProDownloadLink
              data-testid="download-all-video"
              reason="Bulk download is a Pro feature. Upgrade to grab whole shows at once."
              href={downloadAllUrl(showId, "video")}
              className="flex h-10 items-center gap-2 border border-border bg-accent px-4 text-xs font-semibold uppercase tracking-[0.15em] text-accent-foreground transition-colors hover:bg-accent/90"
            >
              <Download className="h-4 w-4" /> All Videos
            </ProDownloadLink>
            <ProDownloadLink
              data-testid="download-all-audio"
              reason="Bulk download is a Pro feature. Upgrade to grab whole shows at once."
              href={downloadAllUrl(showId, "audio")}
              className="flex h-10 items-center gap-2 border border-border bg-transparent px-4 text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground transition-colors hover:border-accent hover:text-accent"
            >
              <Download className="h-4 w-4" /> Audio
            </ProDownloadLink>
            <motion.div whileTap={{ scale: 0.97 }}>
              <Button
                data-testid="remine-btn"
                onClick={doRemine}
                disabled={remining}
                variant="outline"
                className="h-10 rounded-none border-border bg-transparent uppercase tracking-[0.15em] text-xs hover:border-accent hover:text-accent"
              >
                {remining ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                Re-mine
              </Button>
            </motion.div>
          </div>
        )}
      </div>

      {/* Processing */}
      {working && (
        <div data-testid="processing-panel" className="border border-border bg-card p-8">
          <div className="flex items-center gap-3">
            <Loader2 className="h-5 w-5 animate-spin text-accent" />
            <p className="font-display text-2xl tracking-wide text-white">{show.stage_message || "Working..."}</p>
          </div>
          <div className="mt-6 h-1.5 w-full bg-secondary overflow-hidden">
            <div className="h-full bg-primary transition-all duration-700" style={{ width: `${show.progress || 4}%` }} />
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {STAGES.slice(0, 4).map((s, i) => (
              <span
                key={s}
                className={`px-3 py-1 font-mono text-[10px] uppercase tracking-widest ${
                  i < stageIdx ? "bg-accent/20 text-accent" : i === stageIdx ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"
                }`}
              >
                {s}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Failed */}
      {show.status === "failed" && (
        <div data-testid="failed-panel" className="flex items-start gap-3 border border-primary/40 bg-primary/5 p-6">
          <AlertTriangle className="mt-0.5 h-5 w-5 text-primary" />
          <div>
            <p className="font-semibold text-white">Mining failed</p>
            <p className="mt-1 font-mono text-xs text-muted-foreground">{show.error}</p>
            <Button
              data-testid="retry-btn"
              onClick={doRemine}
              className="mt-4 rounded-none bg-primary text-primary-foreground uppercase text-xs tracking-widest"
            >
              Retry
            </Button>
          </div>
        </div>
      )}

      {/* Tabs + content */}
      {show.status === "completed" && (
        <>
          <MomentFinder showId={showId} onCut={pollAfterCut} />
          <div className="flex gap-1 border-b border-border">
            <button
              data-testid="tab-clips"
              onClick={() => setTab("clips")}
              className={`flex items-center gap-2 px-5 py-3 text-xs uppercase tracking-[0.2em] transition-colors ${
                tab === "clips" ? "border-b-2 border-accent text-white" : "text-muted-foreground hover:text-white"
              }`}
            >
              <Scissors className="h-4 w-4" /> Mined Clips
            </button>
            <button
              data-testid="tab-transcript"
              onClick={() => setTab("transcript")}
              className={`flex items-center gap-2 px-5 py-3 text-xs uppercase tracking-[0.2em] transition-colors ${
                tab === "transcript" ? "border-b-2 border-accent text-white" : "text-muted-foreground hover:text-white"
              }`}
            >
              <FileText className="h-4 w-4" /> Transcript
            </button>
          </div>

          {tab === "clips" && (
            clips.length ? (
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 xl:grid-cols-3">
                {clips.map((c, i) => <ClipCard key={c.id} clip={c} show={show} index={i} onUpdated={load} />)}
              </div>
            ) : (
              <div className="flex flex-col items-center gap-3 py-20 text-muted-foreground">
                <Sparkles className="h-8 w-8" />
                <p>No clips were mined from this show.</p>
              </div>
            )
          )}

          {tab === "transcript" && (
            <div data-testid="transcript-view" className="max-h-[70vh] space-y-1 overflow-y-auto border border-border bg-card p-6">
              {(show.segments || []).map((s, i) => (
                <div key={i} className="flex gap-4 py-1 leading-relaxed">
                  <span className="shrink-0 font-mono text-[11px] text-primary/70">{fmt(s.start)}</span>
                  <span className="text-sm text-white/85">{s.text}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};
